import React, { useEffect, useRef, useState } from "react";
import "./Charts.css";

const PAD = { top: 14, right: 16, bottom: 30, left: 44 };
const TICKS = [0, 20, 40, 60, 80, 100];

// Fallback used before the first measurement (and in non-DOM test envs).
const FALLBACK = { width: 620, height: 250 };

/**
 * Single-series line + area over time with a crosshair and hover tooltip.
 * The SVG is measured against its container so it fills the card at any width
 * instead of shrinking on a fixed aspect ratio.
 * `points` is [{ label, value }] with value on a 0-100 scale.
 */
export default function TrendChart({
  points,
  color = "#1b6fd6",
  valueLabel = "Compliance Rate",
  formatValue = (value) => `${value}%`,
}) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState(FALLBACK);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({
        width: Math.max(width, 260),
        height: Math.max(height, 200),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  const x = (index) =>
    PAD.left +
    (points.length === 1 ? plotW / 2 : (index / (points.length - 1)) * plotW);
  const y = (value) => PAD.top + plotH - (value / 100) * plotH;

  const line = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${x(index)},${y(point.value)}`)
    .join(" ");

  const area = `${line} L${x(points.length - 1)},${PAD.top + plotH} L${x(0)},${
    PAD.top + plotH
  } Z`;

  // Nearest point to the cursor, in viewBox units.
  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const position = ((event.clientX - rect.left) / rect.width) * width;
    let nearest = 0;
    points.forEach((_, index) => {
      if (Math.abs(x(index) - position) < Math.abs(x(nearest) - position)) {
        nearest = index;
      }
    });
    setHover(nearest);
  };

  const active = hover === null ? null : points[hover];
  // Flip the tooltip to the left of the crosshair near the right edge.
  const tooltipFlipped = hover !== null && x(hover) > PAD.left + plotW * 0.62;

  return (
    <div className="trend" ref={wrapRef}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="trend__svg"
        role="img"
        aria-label={`${valueLabel} by month, ${points[0]?.label} to ${
          points[points.length - 1]?.label
        }`}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.16" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Recessive gridlines and axis labels */}
        {TICKS.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={PAD.left + plotW}
              y1={y(tick)}
              y2={y(tick)}
              className="trend__grid"
            />
            <text
              x={PAD.left - 10}
              y={y(tick)}
              className="trend__tick trend__tick--y"
            >
              {tick}%
            </text>
          </g>
        ))}

        {points.map((point, index) => (
          <text
            key={point.label}
            x={x(index)}
            y={height - 8}
            className="trend__tick"
          >
            {point.label}
          </text>
        ))}

        <path d={area} fill="url(#trendFill)" />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {points.map((point, index) => (
          <circle
            key={point.label}
            cx={x(index)}
            cy={y(point.value)}
            r={hover === index ? 5.5 : 4}
            fill={color}
            stroke="#fff"
            strokeWidth="2"
          />
        ))}

        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            className="trend__crosshair"
          />
        )}
      </svg>

      {active && (
        <div
          className={`trend__tooltip${
            tooltipFlipped ? " trend__tooltip--flipped" : ""
          }`}
          style={{
            left: `${(x(hover) / width) * 100}%`,
            top: `${(y(active.value) / height) * 100}%`,
          }}
        >
          <p className="trend__tooltip-title">{active.label}</p>
          <p className="trend__tooltip-row">
            <span
              className="trend__tooltip-dot"
              style={{ background: color }}
            />
            {valueLabel}:<strong>{formatValue(active.value)}</strong>
          </p>
        </div>
      )}
    </div>
  );
}
